import { QueryFacts } from '@/components/hql/QueryFacts';
import { QueryInput } from '@/components/hql/QueryInput';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    type CompiledHql,
    checkHql,
    evaluateHql,
    type HomeDocument,
    type HqlCatalog,
    type HqlEvaluation,
    hqlFacts,
} from '@rock/hql';
import type { SpaceId } from '@rock/types';
import type { FC } from 'react';
import { useMemo } from 'react';

/**
 * Type any query and see what it does in this home as you type: problems inline, or what it matched.
 * Custom types of this home are in scope, as they are for its custom reminders.
 */
export const QueryBoxCard: FC<{
    doc: HomeDocument;
    catalog: HqlCatalog;
    query: string;
    /** The space of the consideration a picked query came from, which it also waits on. */
    spaceId?: SpaceId;
    onQueryChange: (query: string) => void;
}> = ({ doc, catalog, query, spaceId, onQueryChange }) => {
    const result = useMemo(
        () => (query.trim() ? checkHql(query, catalog, { spaceId }) : undefined),
        [query, catalog, spaceId],
    );
    const compiled = result?.compiled;
    const evaluation = useMemo(() => (compiled ? evaluateHql(compiled, doc, new Date()) : undefined), [compiled, doc]);
    return (
        <Card>
            <CardHeader>
                <CardTitle>Try a Query</CardTitle>
                <CardDescription>Runs against this home as you type. Nothing is saved.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <QueryInput
                    value={query}
                    onChange={onQueryChange}
                    result={result}
                    placeholder="e.g. furnace-unit[fuelSource == electric] or home[gfci == false]"
                />
                {spaceId ? (
                    <div className="text-xs text-muted-foreground">
                        Also waits for its consideration's space, {spaceId}, as the dry run does. Edit the query to drop
                        that.
                    </div>
                ) : null}
                {compiled && evaluation ? <QueryOutcome compiled={compiled} doc={doc} evaluation={evaluation} /> : null}
            </CardContent>
        </Card>
    );
};

const QueryOutcome: FC<{ compiled: CompiledHql; doc: HomeDocument; evaluation: HqlEvaluation }> = ({
    compiled,
    doc,
    evaluation,
}) => (
    <div className="space-y-4">
        <OutcomeLine evaluation={evaluation} coversEquipment={compiled.coversEquipment} />
        <QueryFacts facts={hqlFacts(compiled, doc)} matched={new Set(evaluation.covers)} />
    </div>
);

const OutcomeLine: FC<{ evaluation: HqlEvaluation; coversEquipment: boolean }> = ({ evaluation, coversEquipment }) => {
    if (evaluation.status === 'pending') {
        return (
            <div className="text-sm text-amber-700">
                Pending: waits until this home completes {evaluation.missingSpaceIds.join(', ')}.
            </div>
        );
    }
    if (evaluation.status === 'does-not-apply') {
        return <div className="text-sm font-medium">Nothing in this home matches.</div>;
    }
    const count = evaluation.covers.length;
    return (
        <div className="text-sm font-medium text-green-700">
            {coversEquipment
                ? `Applies, covering ${count} ${count === 1 ? 'unit' : 'units'}.`
                : 'Applies to the whole home (it covers no equipment).'}
        </div>
    );
};
